import { useForm } from '@tanstack/react-form';
import React, { FC, useCallback, useMemo, useState } from 'react';

import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Checkbox, Icon, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ApiError } from 'client/api/error';
import { publishAliceAgentApp, updateAliceAgentApp } from 'client/api/services/aliceAgentApps';
import { Field } from 'client/blocks/form/Field/Field';
import { wrapInText } from 'client/lib/i18n-utils';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO, AliceAgentAppManifest, AliceInternalAgentAppConfig } from 'client/model/aliceAgentApp';
import { translateAppStoreError } from 'client/utils/errorMessage';
import * as formKeyset from 'i18n/AliceAgentAppForm';

import styles from './AliceAgentAppEditPage.module.scss';
import { AliceAgentAppLogoUploader } from './AliceAgentAppLogoUploader';
import { AliceAgentAppPublishedActions } from './AliceAgentAppPublishedActions';
import { fromManifestMap, LocalizedTextInputs, toManifestMap } from './LocalizedTextInputs';
import { agentAppServerKeyMap, FormState, validators } from './validation';

const tForm = i18n(formKeyset);

interface Props {
    app: AliceAgentAppDTO;
    isDraft: boolean;
    onSaved: () => Promise<void> | void;
}

const readSource = (app: AliceAgentAppDTO, isDraft: boolean) =>
    isDraft
        ? { name: app.draft.name, logo: app.draft.logo, manifest: app.draft.manifest }
        : { name: app.published?.name ?? '', logo: app.published?.logo, manifest: app.published?.manifest };

const toFormState = (app: AliceAgentAppDTO, isDraft: boolean): FormState => {
    const src = readSource(app, isDraft);
    const config = src.manifest?.config as AliceInternalAgentAppConfig | undefined;
    return {
        name: src.name,
        agentName: config?.agentName ?? '',
        title: fromManifestMap(src.manifest?.title),
        shortDescription: fromManifestMap(src.manifest?.shortDescription),
        aliases: (src.manifest?.aliases ?? []).length > 0 ? (src.manifest?.aliases as string[]) : [''],
        availableInPlusMenu: src.manifest?.availableInPlusMenu ?? false,
        logoId: src.logo?.id,
        logoUrl: src.logo?.url,
        chatTestingEnabled: isDraft ? app.draft.chatTestingEnabled : false,
    };
};

const buildManifest = (form: FormState): AliceAgentAppManifest => ({
    type: 'aliceInternalAgentApp',
    config: { agentName: form.agentName } satisfies AliceInternalAgentAppConfig,
    title: toManifestMap(form.title),
    shortDescription: toManifestMap(form.shortDescription),
    aliases: form.aliases.map((s) => s.trim()).filter(Boolean),
    availableInPlusMenu: form.availableInPlusMenu,
});

export const AliceAgentAppEditPage: FC<Props> = ({ app, isDraft, onSaved }) => {
    const { add: toasterAdd } = useToaster();

    const initial = useMemo(() => toFormState(app, isDraft), [app, isDraft]);
    const [isPublishing, setIsPublishing] = useState(false);
    const readOnly = !isDraft;

    const form = useForm({
        defaultValues: initial,
        onSubmit: async ({ value, formApi }) => {
            try {
                await updateAliceAgentApp(app.skillId, {
                    name: value.name,
                    manifest: buildManifest(value),
                    chatTestingEnabled: value.chatTestingEnabled,
                    ...(value.logoId !== initial.logoId && value.logoId ? { logoId: value.logoId } : {}),
                });
                await onSaved();
                toasterAdd({ title: tForm('toast-saved'), theme: 'success', name: 'SAVE_ALICE_AGENT_APP' });
                // Reset baseline so isDirty drops back to false after the DTO reload.
                formApi.reset(value);
            } catch (err) {
                let toastMessage = tForm('toast-save-error');
                if (err instanceof ApiError && err.payload?.typedErrors?.length) {
                    const fieldErrors: Partial<Record<keyof FormState, string>> = {};
                    for (const apiErr of err.payload.typedErrors) {
                        if (!apiErr.key) continue;
                        const fieldKey = agentAppServerKeyMap[apiErr.key];
                        if (!fieldKey) continue;
                        fieldErrors[fieldKey] = translateAppStoreError(apiErr.type, apiErr.arg);
                    }
                    formApi.setErrorMap({
                        onSubmit: { form: '', fields: fieldErrors },
                    });
                    const first = err.payload.typedErrors[0];
                    toastMessage = translateAppStoreError(first.type, first.arg);
                    // Scroll to the first invalid field after React paints the aria-invalid attribute.
                    requestAnimationFrame(() => {
                        const invalid = document.querySelector('[aria-invalid="true"]');
                        if (invalid) {
                            invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            if (invalid instanceof HTMLElement) invalid.focus({ preventScroll: true });
                        }
                    });
                } else if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
                    toastMessage = err.message;
                }
                toasterAdd({ title: toastMessage, theme: 'danger', name: 'SAVE_ALICE_AGENT_APP' });
            }
        },
    });

    const handleLogoUploaded = useCallback(
        (image: { id: string; url: string }) => {
            form.setFieldValue('logoId', image.id);
            form.setFieldValue('logoUrl', image.url);
        },
        [form],
    );

    const handleLogoError = (message: string) => {
        toasterAdd({ title: message, theme: 'danger', name: 'UPLOAD_ALICE_AGENT_APP_LOGO' });
    };

    const handlePublish = async () => {
        if (!isDraft || isPublishing || form.state.isDirty) return;
        setIsPublishing(true);
        try {
            await publishAliceAgentApp(app.skillId);
            await onSaved();
            toasterAdd({ title: tForm('toast-published'), theme: 'success', name: 'PUBLISH_ALICE_AGENT_APP' });
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : tForm('toast-publish-error');
            toasterAdd({ title: message, theme: 'danger', name: 'PUBLISH_ALICE_AGENT_APP' });
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <>
            <form
                className={styles.formContainer}
                id="main"
                onSubmit={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                }}
            >
                <form.Field name="name" validators={{ onChange: validators.name, onSubmit: validators.name }}>
                    {(field) => (
                        <Field required fieldTitle={tForm('field-name')} error={field.state.meta.errors[0]}>
                            <TextInput
                                className={styles.formElement}
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                onBlur={field.handleBlur}
                                disabled={readOnly}
                                size="xl"
                                placeholder={tForm('field-name-placeholder')}
                                validationState={field.state.meta.errors[0] ? 'invalid' : undefined}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="logoId" validators={{ onChange: validators.logoId, onSubmit: validators.logoId }}>
                    {(field) => (
                        <Field required fieldTitle={tForm('field-logo')} error={field.state.meta.errors[0]}>
                            <form.Subscribe selector={(s) => s.values.logoUrl}>
                                {(logoUrl) => (
                                    <AliceAgentAppLogoUploader
                                        skillId={app.skillId}
                                        logoUrl={logoUrl}
                                        disabled={readOnly}
                                        onUploaded={handleLogoUploaded}
                                        onError={handleLogoError}
                                    />
                                )}
                            </form.Subscribe>
                        </Field>
                    )}
                </form.Field>

                <form.Field
                    name="agentName"
                    validators={{ onChange: validators.agentName, onSubmit: validators.agentName }}
                >
                    {(field) => (
                        <Field
                            required
                            fieldTitle={tForm('field-agent-name')}
                            fieldDescription={tForm('field-agent-name-description')}
                            error={field.state.meta.errors[0]}
                        >
                            <TextInput
                                className={styles.formElement}
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                onBlur={field.handleBlur}
                                disabled={readOnly}
                                size="xl"
                                placeholder={tForm('field-agent-name-placeholder')}
                                validationState={field.state.meta.errors[0] ? 'invalid' : undefined}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="title">
                    {(field) => (
                        <Field fieldTitle={tForm('field-title')} fieldDescription={tForm('field-title-description')}>
                            <LocalizedTextInputs
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="shortDescription">
                    {(field) => (
                        <Field
                            fieldTitle={tForm('field-short-description')}
                            fieldDescription={tForm('field-short-description-description')}
                        >
                            <LocalizedTextInputs
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="aliases" mode="array">
                    {(field) => (
                        <Field
                            fieldTitle={tForm('field-aliases')}
                            fieldDescription={wrapInText(
                                (s) => <kbd className={styles.kbd}>{s}</kbd>,
                                (s) => <kbd className={styles.kbd}>{s}</kbd>,
                            )(tForm('field-aliases-description'))}
                        >
                            {field.state.value.map((_, i) => (
                                <form.Field key={i} name={`aliases[${i}]`}>
                                    {(subField) => (
                                        <TextInput
                                            className={styles.formElement}
                                            value={subField.state.value as string}
                                            onUpdate={subField.handleChange}
                                            endContent={
                                                field.state.value.length > 1 ? (
                                                    <Button
                                                        view="flat"
                                                        disabled={readOnly}
                                                        onClick={() => field.removeValue(i)}
                                                    >
                                                        <Icon data={Xmark} />
                                                    </Button>
                                                ) : undefined
                                            }
                                            size="xl"
                                            disabled={readOnly}
                                            placeholder={tForm('field-aliases-placeholder')}
                                        />
                                    )}
                                </form.Field>
                            ))}
                            <Button
                                className={styles.formButton}
                                disabled={readOnly}
                                onClick={() => field.pushValue('')}
                            >
                                <Icon data={Plus} />
                                {tForm('field-aliases-add')}
                            </Button>
                        </Field>
                    )}
                </form.Field>

                <form.Field name="availableInPlusMenu">
                    {(field) => (
                        <Field fieldTitle={tForm('field-placement')}>
                            <Checkbox
                                className={styles.formElement}
                                checked={field.state.value}
                                onChange={(e) => field.handleChange(e.target.checked)}
                                disabled={readOnly}
                                size="l"
                            >
                                {tForm('field-available-in-plus-menu')}
                            </Checkbox>
                        </Field>
                    )}
                </form.Field>

                <form.Field name="chatTestingEnabled">
                    {(field) => (
                        <Field fieldTitle={tForm('field-chat-testing')}>
                            <Checkbox
                                className={styles.formElement}
                                checked={field.state.value}
                                onChange={(e) => field.handleChange(e.target.checked)}
                                disabled={readOnly}
                                size="l"
                            >
                                {tForm('field-chat-testing-label')}
                            </Checkbox>
                        </Field>
                    )}
                </form.Field>
            </form>

            {isDraft && (
                <form.Subscribe selector={(s) => [s.isDirty, s.isSubmitting] as const}>
                    {([isDirty, isSubmitting]) => (
                        <div className={styles.footer}>
                            <Button
                                view="action"
                                size="l"
                                onClick={() => form.handleSubmit()}
                                disabled={!isDirty || isSubmitting || isPublishing}
                                loading={isSubmitting}
                            >
                                {tForm('action-save')}
                            </Button>
                            <Button
                                view="outlined-action"
                                size="l"
                                onClick={handlePublish}
                                disabled={isDirty || isSubmitting || isPublishing}
                                loading={isPublishing}
                            >
                                {tForm('action-publish')}
                            </Button>
                        </div>
                    )}
                </form.Subscribe>
            )}
            {!isDraft && (
                <AliceAgentAppPublishedActions
                    app={app}
                    draftUrl={routes.app.aliceAgentApp(app.skillId, true)}
                    onSaved={onSaved}
                />
            )}
        </>
    );
};
